import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import '../styles/OrdersPage.css';
import SearchBar from '../components/SearchBar';
import { getOverallStatus } from '../utils/orderUtils';
import { useAppData } from '../context/AppDataContext';

export default function OrdersPage() {
  const { allOrders, clients: cachedClients, loaded } = useAppData();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previews, setPreviews] = useState({});
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || '';
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [printerFilter, setPrinterFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [attachmentFilter, setAttachmentFilter] = useState('');
  const [analyticsData, setAnalyticsData] = useState([]);
  // User-toggled month open/closed overrides: { [monthKey]: boolean }
  const [monthOverrides, setMonthOverrides] = useState({});
  
  const navigate = useNavigate();

  // Date formatting utility
  const formatDate = (rawDate) => {
    if (!rawDate) return 'N/A';
    const date = new Date(rawDate);
    const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    
    if (isNaN(localDate)) return 'N/A';
    const mm = String(localDate.getMonth() + 1).padStart(2, '0');
    const dd = String(localDate.getDate()).padStart(2, '0');
    const yy = String(localDate.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  // Parse any raw date into a local, date-only JS Date (00:00 local time)
  const toLocalDateOnly = (raw) => {
    if (!raw) return null;

    if (typeof raw?.toDate === 'function') {
      const d = raw.toDate();
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    if (typeof raw === 'string') {
      const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) {
        const [, y, mo, da] = m;
        return new Date(Number(y), Number(mo) - 1, Number(da));
      }
      const d = new Date(raw);
      return isNaN(d) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    const d = raw instanceof Date ? raw : new Date(raw);
    return isNaN(d) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Returns earliest local date-only drop.startDate, or null
  const getEarliestDropDate = (drops) => {
    if (!Array.isArray(drops) || drops.length === 0) return null;
    const dates = drops.map(d => toLocalDateOnly(d?.startDate)).filter(Boolean);
    if (dates.length === 0) return null;
    return new Date(Math.min(...dates.map(dt => dt.getTime())));
  };

  const getArtDueDate = (drops) => {
    const earliest = getEarliestDropDate(drops);
    if (!earliest) return 'N/A';
    const d = new Date(earliest);
    d.setDate(d.getDate() - 12);
    return formatDate(d);
  };

  // Always use allOrders so all months are visible - closed months are collapsed by default
  useEffect(() => {
    if (!loaded) return;
    setOrders(allOrders);
    setClients(cachedClients);
    setLoading(false);
  }, [loaded, allOrders, cachedClients]);

// Analytics still needs its own fetch since it's not in the global cache:
useEffect(() => {
  getDocs(collection(db, 'analytics'))
    .then(snap => setAnalyticsData(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
    .catch(err => console.error('Error fetching analytics:', err));
}, []);

  // When search/filters change, clear manual month toggles so months with
  // results auto-open (and defaults return once search is cleared)
  useEffect(() => {
    setMonthOverrides({});
  }, [search, statusFilter, printerFilter, sizeFilter, attachmentFilter]);

  // Fast client lookup (replaces repeated clients.find scans)
  const clientsById = useMemo(
    () => new Map(clients.map(c => [c.id, c])),
    [clients]
  );

  // Precompute average response rate per order once, instead of
  // looping the full analytics collection per order per render
  const responseRateByOrder = useMemo(() => {
    const rates = new Map(); // orderId -> array of rates
    analyticsData.forEach(record => {
      if (record.assignments && record.campaigns) {
        record.campaigns.forEach(campaign => {
          const orderId = record.assignments[campaign.title];
          if (orderId) {
            const rate = parseFloat(campaign.responseRate?.replace('%', '')) || 0;
            if (rate > 0) {
              if (!rates.has(orderId)) rates.set(orderId, []);
              rates.get(orderId).push(rate);
            }
          }
        });
      }
    });
    const averages = new Map();
    rates.forEach((list, orderId) => {
      const avg = list.reduce((sum, r) => sum + r, 0) / list.length;
      averages.set(orderId, avg.toFixed(2));
    });
    return averages;
  }, [analyticsData]);

  // Dynamically derive unique filter options from actual order data
  const filterOptions = useMemo(() => {
    const printers = new Set();
    const sizes = new Set();
    const attachments = new Set();

    orders.forEach(order => {
      const printer = order.printer === 'Other' ? order.printerOther : order.printer;
      const size = order.size === 'Other' ? order.sizeOther : order.size;
      const attachment = order.attachment;

      if (printer?.trim()) printers.add(printer.trim());
      if (size?.trim()) sizes.add(size.trim());
      if (attachment?.trim() && attachment !== 'N/A') attachments.add(attachment.trim());
    });

    return {
      printers: [...printers].sort(),
      sizes: [...sizes].sort(),
      attachments: [...attachments].sort(),
    };
  }, [orders]);

  // Helper to resolve the display value of a field (handles "Other" + custom)
  const resolveField = (value, customValue) => {
    if (value === 'Other') return customValue?.trim() || 'Other';
    return value?.trim() || '';
  };

  // Enhanced unified filter function
  const filtered = useMemo(() => orders.filter(order => {
    const client = clientsById.get(order.clientId) || {};
    
    // Unified search
    const searchTerm = (search || '').toString().toLowerCase();
    if (searchTerm) {
      const searchableFields = [
        order.jobNumber,
        order.id,
        order.campaignName,
        order.campaignNameOther,
        order.eventTitle,
        order.artName,
        order.size,
        order.sizeOther,
        order.artSize,
        order.dimensions,
        client.name,
        client.dealershipName,
        client.manufacturer,
        order.manufacturer,
        order.printer,
        order.printerOther,
        order.printingSpecs,
        order.sort,
        order.sortOther,
        order.attachment,
        order.notes,
        order.quantity,
        order.status,
        getOverallStatus(order),
        ...(order.vehicles || []).flatMap(vehicle => [
          vehicle.year,
          vehicle.make,
          vehicle.customMake,
          vehicle.model,
          vehicle.stockNumber
        ]),
        ...(order.prizes || []).flatMap(prize => [
          prize.prize,
          prize.custom,
          prize.value
        ])
      ].filter(Boolean);
      
      const searchableText = searchableFields.join(' ').toLowerCase();
      if (!searchableText.includes(searchTerm)) return false;
    }

    // Status filter
    const overallStatus = order.status || getOverallStatus(order);
    const excludedStatuses = ['On Hold', 'Cancelled', 'Order Complete'];

    const matchesStatus =
      statusFilter === 'Missing Insurance Policy'
        ? !order.insuranceFile && !excludedStatuses.includes(overallStatus)
        : statusFilter === 'Open Orders'
        ? overallStatus !== 'Order Complete'
        : !statusFilter || overallStatus === statusFilter;

    if (!matchesStatus) return false;

    // Printer filter
    if (printerFilter) {
      const orderPrinter = resolveField(order.printer, order.printerOther);
      if (orderPrinter !== printerFilter) return false;
    }

    // Size filter
    if (sizeFilter) {
      const orderSize = resolveField(order.size, order.sizeOther);
      if (orderSize !== sizeFilter) return false;
    }

    // Attachment filter
    if (attachmentFilter) {
      const orderAttachment = order.attachment?.trim() || '';
      if (orderAttachment !== attachmentFilter) return false;
    }

    return true;
  }), [orders, clientsById, search, statusFilter, printerFilter, sizeFilter, attachmentFilter]);

  // Proof fetching for hover previews
  const fetchProofsForOrder = async (orderId) => {
    try {
      const proofRef = collection(db, 'orders', orderId, 'proofs');
      const snap = await getDocs(proofRef);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ta = a.uploadedAt?.toDate?.().getTime() || 0;
        const tb = b.uploadedAt?.toDate?.().getTime() || 0;
        return tb - ta;
      });
      return list;
    } catch (err) {
      console.error('Error fetching proofs for', orderId, err);
      return [];
    }
  };

  // Helper functions
  const getClient = id => clientsById.get(id) || {};
  const getClientLogo = id => {
    const client = getClient(id);
    return client.blackLogo || client.logo || '';
  };

  // Average response rate for an order (precomputed in responseRateByOrder)
  const getOrderResponseRate = (orderId) => responseRateByOrder.get(orderId) || null;

  const handleHover = async (order) => {
    if (previews[order.id] !== undefined) return;
    const proofs = await fetchProofsForOrder(order.id);
    const url = proofs[0]?.fileUrl || proofs[0]?.url || '';
    setPreviews(prev => ({ ...prev, [order.id]: url }));
  };

  const renderPreviewContent = (order) => {
    const url = previews[order.id];
    if (!url) return <span className="orders-preview-empty">No proof</span>;
    
    const clean = url.split('?')[0].toLowerCase();
    const isPdf = clean.endsWith('.pdf');
    
    return isPdf ? (
      <object data={url} type="application/pdf" className="orders-proof-object">
        <span className="orders-preview-empty">PDF Preview</span>
      </object>
    ) : (
      <img src={url} alt="Proof" className="orders-proof-img" loading="lazy" />
    );
  };

  const renderDropDates = (drops) => {
    if (!drops || drops.length === 0) return null;
    
    return (
      <div className="orders-drop-dates">
        {drops.map((drop, idx) => (
          <div key={idx} className="orders-drop-date-item">
            <small>Drop {idx + 1}: {formatDate(drop.startDate)} - {formatDate(drop.endDate)}</small>
          </div>
        ))}
      </div>
    );
  };

  // Count active filters (excluding search)
  const activeFilterCount = [statusFilter, printerFilter, sizeFilter, attachmentFilter].filter(Boolean).length;

  // Clear all filters function
  const clearAllFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPrinterFilter('');
    setSizeFilter('');
    setAttachmentFilter('');
  };

  if (loading) return <div className="orders-loading">Loading orders...</div>;

  // Group orders by the month of their FIRST DROP (fallback to createdAt if no drops)
  const todayMonthKey = new Date().toLocaleString('default', { year: 'numeric', month: 'long' });

  const byMonth = filtered.reduce((acc, order) => {
    const earliestDrop = getEarliestDropDate(order.drops);

    const createdAt =
      order?.createdAt && typeof order.createdAt.toDate === 'function'
        ? toLocalDateOnly(order.createdAt)
        : null;

    const dateForGrouping = earliestDrop || createdAt;

    const monthKey = dateForGrouping
      ? dateForGrouping.toLocaleString('default', { year: 'numeric', month: 'long' })
      : 'Unscheduled';

    (acc[monthKey] ||= []).push(order);
    return acc;
  }, {});

  const monthKeyToDate = (key) => {
    if (key === 'Unscheduled') return new Date(0);
    // Parse "Month YYYY" reliably (e.g. "December 2025") to avoid invalid date strings
    const parts = key.split(' ');
    const monthIndex = new Date(parts[0] + ' 1, 2000').getMonth();
    return new Date(parseInt(parts[1], 10), monthIndex, 1);
  };
  const monthKeys = Object.keys(byMonth).sort((a, b) => monthKeyToDate(b) - monthKeyToDate(a));

  return (
    <div className="orders-page-container">
      <div className="orders-page-header">
        <h1 className="orders-gradient-text">Orders</h1>
        
        <div className="orders-page-controls">
          {/* Unified Search Bar */}
          <div className="orders-search-container">
            <SearchBar
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 Search anything..."
            />
            {search && (
              <div className="orders-search-help">
                <small>
                  Searching across all fields including custom entries. 
                  Try: job numbers, campaign names, sizes, dealerships, manufacturers, printers, vehicles, prizes, etc.
                </small>
              </div>
            )}
          </div>
          
          {/* Filter Row */}
          <div className="orders-filter-controls">
            {/* Status */}
            <div className="orders-status-filter">
              <label>Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="Open Orders">Open Orders</option>
                <option value="Incomplete - Need Art">Incomplete - Need Art</option>
                <option value="Pending Admin Approval">Pending Admin Approval</option>
                <option value="Pending Client Approval">Pending Client Approval</option>
                <option value="Art Complete - No Insurance">Art Complete - No Insurance</option>
                <option value="Art Complete - No Prizeboard">Art Complete - No Prizeboard</option>
                <option value="Pending Prizeboard Approval">Pending Prizeboard Approval</option>
                <option value="Order Complete">Order Complete</option>
                <option value="On Hold">On Hold</option>
                <option value="Cancelled">Cancelled</option>
                <option value="Missing Insurance Policy">Missing Insurance Policy</option>
              </select>
            </div>

            {/* Printer */}
            <div className="orders-status-filter">
              <label>Printer</label>
              <select
                value={printerFilter}
                onChange={(e) => setPrinterFilter(e.target.value)}
              >
                <option value="">All</option>
                {filterOptions.printers.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Size */}
            <div className="orders-status-filter">
              <label>Size</label>
              <select
                value={sizeFilter}
                onChange={(e) => setSizeFilter(e.target.value)}
              >
                <option value="">All</option>
                {filterOptions.sizes.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Attachment */}
            <div className="orders-status-filter">
              <label>Attachment</label>
              <select
                value={attachmentFilter}
                onChange={(e) => setAttachmentFilter(e.target.value)}
              >
                <option value="">All</option>
                {filterOptions.attachments.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>

            <div className="orders-action-buttons">
              {(search || activeFilterCount > 0) && (
                <button 
                  onClick={clearAllFilters}
                  className="orders-clear-btn"
                >
                  Clear {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
                </button>
              )}
              
              <button 
                className="orders-add-btn"
                onClick={() => navigate('/orders/new')}
              >
                Add New Order
              </button>
            </div>
          </div>

          {/* Active filter tags */}
          {activeFilterCount > 0 && (
            <div className="orders-active-filters">
              {statusFilter && (
                <span className="orders-filter-tag">
                  Status: {statusFilter}
                  <button onClick={() => setStatusFilter('')}>×</button>
                </span>
              )}
              {printerFilter && (
                <span className="orders-filter-tag">
                  Printer: {printerFilter}
                  <button onClick={() => setPrinterFilter('')}>×</button>
                </span>
              )}
              {sizeFilter && (
                <span className="orders-filter-tag">
                  Size: {sizeFilter}
                  <button onClick={() => setSizeFilter('')}>×</button>
                </span>
              )}
              {attachmentFilter && (
                <span className="orders-filter-tag">
                  Attachment: {attachmentFilter}
                  <button onClick={() => setAttachmentFilter('')}>×</button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="orders-months-container">
        {monthKeys.map(monthKey => {
          // User toggle wins; otherwise default open for current month or active search/filters
          const isOpen =
            monthOverrides[monthKey] ??
            (monthKey === todayMonthKey || !!search || activeFilterCount > 0);

          // Closed months render only the header - no folders, no images, no sorting
          if (!isOpen) {
            return (
              <details key={monthKey} open={false} className="orders-month-section">
                <summary
                  className="orders-month-header"
                  onClick={(e) => {
                    e.preventDefault();
                    setMonthOverrides(prev => ({ ...prev, [monthKey]: true }));
                  }}
                >
                  {monthKey} ({byMonth[monthKey].length} orders)
                </summary>
              </details>
            );
          }

          const monthOrders = byMonth[monthKey].slice().sort((a, b) => {
            if (a.artUploaded !== b.artUploaded) return a.artUploaded ? 1 : -1;

            const ta = getEarliestDropDate(a.drops)?.getTime() ?? 0;
            const tb = getEarliestDropDate(b.drops)?.getTime() ?? 0;

            if (ta === tb) {
              const ca = a.createdAt?.toDate?.().getTime() || 0;
              const cb = b.createdAt?.toDate?.().getTime() || 0;
              return cb - ca;
            }

            return ta - tb;
          });

          return (
            <details 
              key={monthKey} 
              open={true} 
              className="orders-month-section"
            >
              <summary
                className="orders-month-header"
                onClick={(e) => {
                  e.preventDefault();
                  setMonthOverrides(prev => ({ ...prev, [monthKey]: false }));
                }}
              >
                {monthKey} ({monthOrders.length} orders)
              </summary>
              
              <div className="orders-folder-grid">
                {monthOrders.map(order => (
                  <div 
                    key={order.id} 
                    className="orders-folder-wrapper" 
                    onMouseEnter={() => handleHover(order)}
                  >
                    {/* Folder Visual */}
                    <div 
                      className="orders-folder" 
                      onClick={() => navigate(`/orders/${order.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          navigate(`/orders/${order.id}`);
                        }
                      }}
                    >
                      <div className="orders-folder__back" />
                      <div className="orders-folder__front" />
                      {/* Dealership Name Overlay Inside Folder */}
                      <div className="orders-dealership-overlay">
                        {getClient(order.clientId)?.name || 'Unknown Dealership'}
                        
                        {/* Response Rate Display Inside Folder */}
                        {(() => {
                          const responseRate = getOrderResponseRate(order.id);
                          return responseRate ? (
                            <div 
                              style={{
                                marginTop: '8px',
                                padding: '4px 8px',
                                backgroundColor: '#ff9800',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '700',
                                color: '#ffffff',
                                textAlign: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}
                            >
                              {responseRate}% Response Rate
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* Preview Container */}
                    <div className="orders-preview-container">
                      <div className="orders-preview-box orders-preview-logo">
                        {getClientLogo(order.clientId) ? (
                          <img src={getClientLogo(order.clientId)} alt="Dealer logo" loading="lazy" />
                        ) : (
                          <span className="orders-preview-empty">No logo</span>
                        )}
                      </div>
                      <div className="orders-preview-box orders-preview-proof">
                        {renderPreviewContent(order)}
                      </div>
                    </div>

                    {/* Job Information */}
                    <div className="orders-job-info">
                      <div className="orders-job-label">
                        <strong>{order.jobNumber || order.id}</strong>
                      </div>
                      
                      <div 
                        className="orders-job-status"
                        style={{
                          color: (order.status || getOverallStatus(order)) === 'Order Complete' ? '#22c55e' : 
                                 (order.status || getOverallStatus(order)).includes('Pending') ? '#0011ffff' :
                                 ['On Hold', 'Cancelled'].includes(order.status || getOverallStatus(order)) ? '#6b7280' :
                                 '#ef4444',
                          fontSize: (order.status || getOverallStatus(order)) === 'Order Complete' ? '12px' : 'normal'
                        }}
                      >
                        {order.status || getOverallStatus(order)}
                      </div>

                      {order.drops?.length > 0 && (
                        <>
                          {renderDropDates(order.drops)}
                          {(order.status || getOverallStatus(order)) !== 'Order Complete' && (
                          <div className="orders-art-due-date">
                            <small>Art Due: {getArtDueDate(order.drops)}</small>
                          </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          );
        })}
      </div>

      {filtered.length === 0 && !loading && (
        <div className="orders-no-results">
          <p>No orders found matching your criteria.</p>
          {(search || activeFilterCount > 0) && (
            <button onClick={clearAllFilters} className="orders-add-btn">
              Clear All Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}